import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { Roles } from '@app/core/guard/Roles';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import {
  MeetingInsightsDto,
  MeetingSuggestedLinkDto,
} from '@interfaces/meetings/responses/meeting-insights.response';
import {
  MeetingArtifact,
  MeetingDealLinkDto,
  MeetingDto,
  MeetingLinkDto,
  MeetingTranscriptSegmentDto,
} from '@interfaces/meetings/responses/meeting.response';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { MeetingsHttpService } from '@services/http/meetings/meetings-http.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { TabViewComponent } from '@src/app/shared/components/tab-view/tab-view.component';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Observable, Subscription, filter, finalize, first, timer } from 'rxjs';
import {
  MeetingLifecycleAction,
  meetingLifecycleTexts,
} from '@features/dashboard/meetings/components/meeting-lifecycle-actions';
import { ModalLinkMeetingComponent } from '@features/dashboard/meetings/components/modal-link-meeting/modal-link-meeting.component';
import {
  mensagemDoErro,
  rotuloDoDealVinculado,
  rotuloDoVinculo,
} from '@features/dashboard/meetings/components/meeting-presenter';
import { MeetingChaptersComponent } from './components/meeting-chapters/meeting-chapters.component';
import { MeetingDetailHeaderComponent } from './components/meeting-detail-header/meeting-detail-header.component';
import { MeetingDetailSideComponent } from './components/meeting-detail-side/meeting-detail-side.component';
import { MeetingLinksPanelComponent } from './components/meeting-links-panel/meeting-links-panel.component';
import { MeetingPlayerComponent } from './components/meeting-player/meeting-player.component';
import { MeetingSummaryPanelComponent } from './components/meeting-summary-panel/meeting-summary-panel.component';
import { MeetingTranscriptPanelComponent } from './components/meeting-transcript-panel/meeting-transcript-panel.component';
import { ModalShareMeetingComponent } from '@features/dashboard/meetings/pages/meeting-detail/components/modal-share-meeting/modal-share-meeting.component';
import { ModalMeetingVisibilityComponent } from '@features/dashboard/meetings/pages/meeting-detail/components/modal-meeting-visibility/modal-meeting-visibility.component';
import { MeetingVisibilityRulesDrawerComponent } from '@features/dashboard/meetings/components/meeting-visibility-rules-drawer/meeting-visibility-rules-drawer.component';
import { ModalMeetingAgentRunsComponent } from './components/modal-meeting-agent-runs/modal-meeting-agent-runs.component';
import { processamentoDaGravacao } from './meeting-detail-state';
import { insightsEmAndamento } from './meeting-insights-state';

/** Reunião futura, cancelada ou remarcada: não há transcrição a esperar. */
const SEM_TRANSCRICAO_A_ESPERAR: readonly string[] = [
  'Scheduled',
  'BotCancelled',
  'Cancelled',
  'Rescheduled',
];
/** De quanto em quanto tempo a tela se atualiza enquanto a gravação processa. */
const ATUALIZAR_A_CADA_MS = 30_000;
/**
 * Quantas atualizações automáticas seguidas, no máximo (cerca de 20 min). Transcrição que falhou de vez
 * deixaria a tela "a caminho" consultando para sempre; depois disso, quem quiser recarrega a página.
 */
const MAXIMO_DE_ATUALIZACOES = 40;

/** As abas do cartão ao lado do vídeo. */
type Aba = 'transcricao' | 'resumo' | 'vinculos' | 'detalhes';
const ABAS: { aba: Aba; titulo: string }[] = [
  { aba: 'transcricao', titulo: 'Transcrição' },
  { aba: 'resumo', titulo: 'Resumo' },
  { aba: 'vinculos', titulo: 'Vínculos' },
  { aba: 'detalhes', titulo: 'Detalhes' },
];

/**
 * Tela de detalhe de uma meeting (card 3162; redesenho da opção 1 do protótipo, 01/10/2026): o vídeo
 * em 16:9 com os capítulos embaixo e, ao lado, um cartão com as abas **Transcrição** (ligada ao
 * vídeo), **Resumo** e **Vínculos** (o que os agentes do Cog trazem, PBI 3225) e **Detalhes**
 * (participantes e origem do bot). Sem vídeo, o lugar dele explica por quê: processando (com as
 * etapas), reunião futura ou terminada sem gravação, com o motivo que a Recall deu.
 *
 * **Duas rotas, a mesma tela:**
 * - `/dashboard/meetings/:id` — o time interno, pela lista. Com as ações: vincular, desvincular e
 *   "Compartilhar com o time" (acesso geral; só para quem participou e só na call interna ou entrevista,
 *   `canManageAccess`, regra de 07/10/2026).
 * - `/dashboard/commercial/my-deals/:dealId/meetings/:meetingId` — pela aba Ligações do deal, também
 *   para o parceiro (só as reuniões de que ele participou). Só leitura: vídeo, download e transcrição.
 *
 * Reunião futura ou sem gravação abre mesmo assim, com o cabeçalho, os participantes e o link da
 * call. A gravação vem por link temporário (1 h): se expirar com a tela aberta, o vídeo avisa e
 * oferece "Carregar de novo", voltando ao ponto em que estava. Enquanto a gravação processa, a tela
 * se atualiza sozinha a cada 30 s.
 *
 * **Agentes do Cog (PBI 3225).** Com transcrição, a tela pede `insights` (resumo, capítulos e vínculos
 * sugeridos) pela mesma porta da reunião e, enquanto os agentes rodam, consulta de novo a cada 30 s.
 * Sugestão de vínculo é aplicada ou dispensada com um clique; "Gerar de novo" pede outra rodada.
 */
@Component({
  selector: 'app-meeting-detail',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    TabViewComponent,
    ModalLinkMeetingComponent,
    MeetingDetailHeaderComponent,
    MeetingPlayerComponent,
    MeetingChaptersComponent,
    MeetingTranscriptPanelComponent,
    MeetingSummaryPanelComponent,
    MeetingLinksPanelComponent,
    MeetingDetailSideComponent,
    ModalMeetingAgentRunsComponent,
    ModalShareMeetingComponent,
    ModalMeetingVisibilityComponent,
    MeetingVisibilityRulesDrawerComponent,
  ],
  templateUrl: './meeting-detail.component.html',
  styleUrl: './meeting-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingDetailComponent extends VaultBaseComponent implements OnInit {
  @ViewChild('modalVincular') modalVincular: ModalLinkMeetingComponent | null = null;
  @ViewChild('modalSessoes') modalSessoes: ModalMeetingAgentRunsComponent | null = null;
  @ViewChild('modalCompartilhar') modalCompartilhar: ModalShareMeetingComponent | null = null;
  @ViewChild('modalVisibility') modalVisibility: ModalMeetingVisibilityComponent | null = null;
  /** Painel "Como funciona a visibilidade" (card 3803), aberto pela janela de visibilidade. */
  readonly rulesOpen = signal(false);
  private readonly player = viewChild(MeetingPlayerComponent);

  /** Aberta pela aba do deal (rota com `dealId`): só leitura, pelos endpoints do deal. */
  readonly dealId = signal<string | null>(null);
  readonly meetingId = signal('');
  readonly ehInterno = signal(false);
  /** ADMIN abre a sessão do agente no back office (painel "Sessões do Cog"). */
  readonly ehAdmin = signal(false);
  /** Ações do time interno só na rota da visão geral. */
  readonly podeAgir = computed(() => this.ehInterno() && !this.dealId());

  readonly meeting = signal<MeetingDto | null>(null);
  readonly carregando = signal(true);
  readonly erro = signal<string | null>(null);
  readonly ocupada = signal(false);

  readonly videoUrl = signal<string | null>(null);
  readonly videoErro = signal<string | null>(null);
  readonly baixando = signal(false);
  readonly tempoAtual = signal(0);

  readonly falas = signal<MeetingTranscriptSegmentDto[]>([]);
  readonly carregandoTranscricao = signal(false);
  readonly transcricaoErro = signal<string | null>(null);

  /** O que os agentes do Cog produziram (resumo, capítulos, vínculos com trecho da fala). */
  readonly insights = signal<MeetingInsightsDto | null>(null);
  readonly carregandoInsights = signal(false);
  readonly insightsErro = signal<string | null>(null);
  /** "Gerar de novo" em curso. */
  readonly gerandoInsights = signal(false);
  /** Sugestão de vínculo sendo aplicada ou dispensada (um clique, loading só nela). */
  readonly decidindo = signal<string | null>(null);

  protected readonly itensDasAbas: TabViewItem[] = ABAS.map((a, i) => ({
    title: a.titulo,
    tabindex: i,
    content: '',
  }));
  protected readonly abaAtual = signal(0);
  protected readonly aba = computed<Aba>(() => ABAS[this.abaAtual()]?.aba ?? 'transcricao');

  /** Do aviso de gravação parcial (card 3244): abre a aba Detalhes, onde fica a linha do tempo da gravação. */
  protected abrirLinhaDoTempo(): void {
    this.abaAtual.set(ABAS.findIndex(a => a.aba === 'detalhes'));
  }

  /** A gravação ainda está a caminho: a aba Transcrição explica em vez de dizer que não há. */
  protected readonly transcricaoACaminho = computed(() => {
    const m = this.meeting();
    return !!m && !!processamentoDaGravacao(m);
  });
  protected readonly semTranscricaoAEsperar = computed(() =>
    SEM_TRANSCRICAO_A_ESPERAR.includes(this.meeting()?.status ?? '')
  );

  readonly voltarPara = signal('/dashboard/meetings');
  /**
   * O breadcrumb faz o papel do antigo "Voltar": o item de onde se veio (Meetings ou o deal) leva para
   * a lista como estava (aba, filtros e página, pelo `history.state.voltarPara`).
   */
  readonly breadcrumbs = computed<MenuItem[]>(() => {
    const volta = this.router.parseUrl(this.voltarPara());
    const caminho =
      '/' + (volta.root.children['primary']?.segments.map(s => s.path).join('/') ?? '');
    const daVolta = { routerLink: caminho, queryParams: volta.queryParams };
    return this.dealId()
      ? [
          { label: 'Comercial' },
          { label: 'Deals', routerLink: '/dashboard/commercial/my-deals' },
          { label: 'Deal', ...daVolta },
          { label: this.meeting()?.title ?? 'Meeting' },
        ]
      : [
          { label: 'Comercial' },
          { label: 'Meetings', ...daVolta },
          { label: this.meeting()?.title ?? 'Meeting' },
        ];
  });
  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };

  /** Atualização automática enquanto a gravação processa. */
  private atualizacao: Subscription | null = null;
  private atualizacoesSeguidas = 0;
  /** Número da carga em curso: resposta de uma carga anterior (outra rota, por exemplo) é descartada. */
  private carga = 0;

  /** Mesma régua para os insights, com contador e teto próprios. */
  private atualizacaoDosInsights: Subscription | null = null;
  private atualizacoesDosInsights = 0;
  private cargaDosInsights = 0;

  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionsService);

  /** Arquivar / Restaurar no menu "…" (card 3786). */
  readonly canArchive = toSignal(this.permissions.hasPermission(Permissions.Meeting.ARCHIVE), {
    initialValue: false,
  });
  /** Excluir no menu "…" (card 3786); o cabeçalho ainda exige `canDelete` da reunião. */
  readonly canDelete = toSignal(this.permissions.hasPermission(Permissions.Meeting.DELETE), {
    initialValue: false,
  });

  constructor(
    translateService: TranslateService,
    private readonly meetingsService: MeetingsHttpService,
    private readonly permissionsService: PermissionsService,
    private readonly confirmationService: ConfirmationService,
    private readonly toast: ToastHelperService
  ) {
    super(translateService);
  }

  ngOnInit(): void {
    // O breadcrumb leva para a lista como estava (aba, filtros, página), quando veio de lá.
    const voltar = (history.state as { voltarPara?: string } | null)?.voltarPara;

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const dealId = params.get('dealId');
      this.dealId.set(dealId);
      this.meetingId.set(params.get('meetingId') ?? params.get('id') ?? '');
      this.voltarPara.set(
        voltar ?? (dealId ? `/dashboard/commercial/my-deals/${dealId}` : '/dashboard/meetings')
      );
      this.carregar();
    });

    this.permissionsService
      .hasRole(Roles.TENANT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(interno => this.ehInterno.set(interno));

    this.permissionsService
      .hasRole(Roles.ADMIN)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(admin => this.ehAdmin.set(admin));
  }

  /**
   * Carrega a meeting. `emSilencio` é a atualização automática enquanto a gravação processa: não limpa
   * a tela nem mostra o loading, e só pede vídeo e transcrição quando eles acabaram de ficar prontos.
   */
  carregar(emSilencio = false): void {
    this.atualizacao?.unsubscribe();
    this.atualizacao = null;
    if (!emSilencio) this.atualizacoesSeguidas = 0;
    const anterior = emSilencio ? this.meeting() : null;
    const estaCarga = ++this.carga;

    if (!emSilencio) {
      this.carregando.set(true);
      this.erro.set(null);
      this.meeting.set(null);
      this.videoUrl.set(null);
      this.videoErro.set(null);
      this.falas.set([]);
      this.transcricaoErro.set(null);
      this.cargaDosInsights++;
      this.atualizacaoDosInsights?.unsubscribe();
      this.insights.set(null);
      this.insightsErro.set(null);
    }

    const dealId = this.dealId();
    const id = this.meetingId();
    const origem = dealId
      ? this.meetingsService.getDealMeeting(dealId, id)
      : this.meetingsService.getMeeting(id);

    origem.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: meeting => {
        if (estaCarga !== this.carga) return;
        this.carregando.set(false);
        this.meeting.set(meeting);
        if (meeting?.hasRecording && !anterior?.hasRecording) this.carregarVideo();
        if (meeting?.hasTranscript && !anterior?.hasTranscript) {
          this.carregarTranscricao();
          this.carregarInsights();
        }
        if (meeting && processamentoDaGravacao(meeting)) this.agendarAtualizacao();
      },
      error: erro => {
        if (estaCarga !== this.carga) return;
        this.carregando.set(false);
        // Falha numa atualização automática não derruba a tela que já está aberta: tenta de novo.
        if (emSilencio && anterior) {
          this.agendarAtualizacao();
          return;
        }
        this.erro.set(
          mensagemDoErro(
            erro,
            'Não foi possível abrir esta meeting. Ela pode ser privada (sem empresa vinculada): peça a um participante para vinculá-la ou disponibilizá-la.'
          )
        );
      },
    });
  }

  /** Enquanto a gravação processa, a tela se atualiza sozinha (sem piscar). */
  private agendarAtualizacao(): void {
    if (this.atualizacoesSeguidas >= MAXIMO_DE_ATUALIZACOES) return;
    this.atualizacoesSeguidas++;
    this.atualizacao = timer(ATUALIZAR_A_CADA_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.carregar(true));
  }

  // ─────────────────────────── vídeo ───────────────────────────

  carregarVideo(): void {
    this.videoErro.set(null);
    this.linkDaGravacao(false)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resposta => this.videoUrl.set(resposta?.url ?? null),
        error: erro =>
          this.videoErro.set(mensagemDoErro(erro, 'Não foi possível carregar o vídeo.')),
      });
  }

  /** O link expirou (ou a rede caiu): guarda o ponto e oferece carregar de novo. */
  protected videoFalhou(segundos: number): void {
    this.player()?.retomarNo(segundos || this.tempoAtual());
    this.videoUrl.set(null);
    this.videoErro.set('O link do vídeo expirou ou a conexão caiu.');
  }

  /** Clique no tempo da transcrição, num capítulo ou num trecho do Cog: leva o vídeo para lá e toca. */
  protected irPara(segundos: number): void {
    this.player()?.irPara(segundos);
  }

  /** Download de verdade, com o nome amigável que o servidor põe no link (`AAAA-MM-DD HHhMM - título.mp4`). */
  protected baixar(): void {
    this.baixando.set(true);
    this.linkDaGravacao(true)
      .pipe(
        first(),
        finalize(() => this.baixando.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: resposta => {
          if (!resposta?.url) return;
          const link = document.createElement('a');
          link.href = resposta.url;
          link.rel = 'noopener';
          if (resposta.fileName) link.download = resposta.fileName;
          link.click();
        },
        error: erro =>
          this.toast.showToastError(
            'Não foi possível baixar o vídeo',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          ),
      });
  }

  private linkDaGravacao(download: boolean) {
    const dealId = this.dealId();
    return dealId
      ? this.meetingsService.getDealMeetingRecordingUrl(dealId, this.meetingId(), download)
      : this.meetingsService.getRecordingUrl(this.meetingId(), download);
  }

  // ─────────────────────────── transcrição ───────────────────────────

  private carregarTranscricao(): void {
    const dealId = this.dealId();
    const origem = dealId
      ? this.meetingsService.getDealMeetingTranscript(dealId, this.meetingId())
      : this.meetingsService.getTranscript(this.meetingId());

    this.carregandoTranscricao.set(true);
    origem
      .pipe(
        first(),
        finalize(() => this.carregandoTranscricao.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: transcricao => this.falas.set(transcricao?.segments ?? []),
        error: erro =>
          this.transcricaoErro.set(
            mensagemDoErro(erro, 'Não foi possível carregar a transcrição.')
          ),
      });
  }

  // ─────────────────────────── agentes do Cog (PBI 3225) ───────────────────────────

  /**
   * Resumo, capítulos e vínculos sugeridos. `emSilencio` é a consulta automática enquanto os agentes
   * rodam: não mostra loading e falha não apaga o que já está na tela.
   */
  private carregarInsights(emSilencio = false): void {
    this.atualizacaoDosInsights?.unsubscribe();
    this.atualizacaoDosInsights = null;
    if (!emSilencio) this.atualizacoesDosInsights = 0;
    const estaCarga = ++this.cargaDosInsights;
    const dealId = this.dealId();
    const origem = dealId
      ? this.meetingsService.getDealMeetingInsights(dealId, this.meetingId())
      : this.meetingsService.getInsights(this.meetingId());

    if (!emSilencio) {
      this.carregandoInsights.set(true);
      this.insightsErro.set(null);
    }
    origem.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe({
      next: insights => {
        if (estaCarga !== this.cargaDosInsights) return;
        this.carregandoInsights.set(false);
        this.aplicarInsights(insights);
      },
      error: erro => {
        if (estaCarga !== this.cargaDosInsights) return;
        this.carregandoInsights.set(false);
        if (emSilencio && this.insights()) {
          this.agendarInsights();
          return;
        }
        this.insightsErro.set(mensagemDoErro(erro, 'Não foi possível carregar o resumo do Cog.'));
      },
    });
  }

  /** Mostra o que chegou e, se os agentes ainda rodam, agenda a próxima consulta. */
  private aplicarInsights(insights: MeetingInsightsDto | null): void {
    this.insights.set(insights);
    this.insightsErro.set(null);
    if (insightsEmAndamento(insights)) this.agendarInsights();
  }

  private agendarInsights(): void {
    this.atualizacaoDosInsights?.unsubscribe();
    if (this.atualizacoesDosInsights >= MAXIMO_DE_ATUALIZACOES) return;
    this.atualizacoesDosInsights++;
    this.atualizacaoDosInsights = timer(ATUALIZAR_A_CADA_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.carregarInsights(true));
  }

  /** "Gerar de novo" (ou "Gerar resumo"): nova rodada dos dois agentes. Só o time interno. */
  protected gerarInsights(): void {
    if (!this.podeAgir()) return;
    this.gerandoInsights.set(true);
    this.atualizacoesDosInsights = 0;
    this.cargaDosInsights++;
    this.meetingsService
      .reprocessInsights(this.meetingId())
      .pipe(
        first(),
        finalize(() => this.gerandoInsights.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: insights => this.aplicarInsights(insights),
        error: erro => {
          this.toast.showToastError(
            'Não foi possível gerar de novo',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
          // A consulta automática em curso foi descartada: sem isto a aba pararia de se atualizar.
          if (insightsEmAndamento(this.insights())) this.agendarInsights();
        },
      });
  }

  /** Painel discreto "Sessões do Cog": situação, custo e sessão de cada agente (time interno). */
  protected verSessoes(): void {
    this.modalSessoes?.open();
  }

  /** Um clique: grava o vínculo com origem Cog e atualiza a lista de vínculos. */
  protected aplicarSugestao(sugestao: MeetingSuggestedLinkDto): void {
    this.decidirSugestao(
      sugestao,
      this.meetingsService.applyLinkSuggestion(this.meetingId(), sugestao.id),
      { sucesso: 'Vínculo criado.', falha: 'Não foi possível vincular' },
      true
    );
  }

  /** Um clique: a sugestão some e não volta num reprocessamento. */
  protected dispensarSugestao(sugestao: MeetingSuggestedLinkDto): void {
    this.decidirSugestao(
      sugestao,
      this.meetingsService.dismissLinkSuggestion(this.meetingId(), sugestao.id),
      { sucesso: 'Sugestão dispensada.', falha: 'Não foi possível dispensar' },
      false
    );
  }

  private decidirSugestao(
    sugestao: MeetingSuggestedLinkDto,
    acao: Observable<MeetingInsightsDto | null>,
    mensagens: { sucesso: string; falha: string },
    recarregarReuniao: boolean
  ): void {
    this.decidindo.set(sugestao.id);
    this.cargaDosInsights++;
    acao
      .pipe(
        first(),
        finalize(() => this.decidindo.set(null)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: insights => {
          this.toast.showToastSuccess(mensagens.sucesso);
          if (insights) this.aplicarInsights(insights);
          // O vínculo novo entra no grupo do tipo: a lista vem da reunião.
          if (recarregarReuniao) this.carregar(true);
        },
        error: erro => {
          this.toast.showToastError(
            mensagens.falha,
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          );
          if (insightsEmAndamento(this.insights())) this.agendarInsights();
        },
      });
  }

  // ─────────────────────────── ações do time interno ───────────────────────────

  protected alternarAcesso(): void {
    const meeting = this.meeting();
    if (!meeting) return;
    const disponibilizar = !meeting.isShared;

    this.confirmationService
      .confirm(
        disponibilizar ? 'Compartilhar com o time da Legatus?' : 'Voltar a meeting a privada?',
        disponibilizar
          ? 'Todo o time da Legatus passa a ver esta meeting, com o vídeo e a transcrição. Qualquer participante pode desfazer.'
          : 'Só quem participou volta a vê-la.',
        disponibilizar ? 'default' : 'danger',
        undefined,
        disponibilizar ? 'Compartilhar' : 'Voltar a privada'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(this.meetingsService.setGeneralAccess(meeting.id, disponibilizar), {
          sucesso: disponibilizar
            ? 'Meeting disponível para o time da Legatus.'
            : 'Meeting privada de novo.',
          falha: 'Não foi possível mudar o acesso',
        })
      );
  }

  /** Janela Compartilhar (card 3241): pessoas do time interno, link com prazo e quem acessou. */
  protected compartilhar(): void {
    const meeting = this.meeting();
    if (meeting) this.modalCompartilhar?.open(meeting);
  }

  protected vincular(): void {
    const meeting = this.meeting();
    if (meeting) this.modalVincular?.open(meeting);
  }

  /** Selo "Visível para" (card 3803): abre a janela com as três chaves (só leitura para quem não participou). */
  protected openVisibility(): void {
    const meeting = this.meeting();
    if (meeting) this.modalVisibility?.open(meeting);
  }

  protected desvincularDeal(link: MeetingDealLinkDto): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.confirmationService
      .confirm(
        'Desvincular esta meeting do deal?',
        `Ela deixa de aparecer na aba Reuniões do deal ${rotuloDoDealVinculado(link)}, para todos que o veem. Pode ser vinculada de novo.`,
        'danger',
        undefined,
        'Desvincular'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(this.meetingsService.unlinkDeal(meeting.id, link.dealId), {
          sucesso: 'Meeting desvinculada do deal.',
          falha: 'Não foi possível desvincular',
        })
      );
  }

  protected desvincularVinculo(link: MeetingLinkDto): void {
    const meeting = this.meeting();
    if (!meeting) return;
    this.confirmationService
      .confirm(
        'Desfazer este vínculo?',
        `"${rotuloDoVinculo(link)}" deixa de estar vinculado a esta meeting. Pode ser vinculado de novo.`,
        'danger',
        undefined,
        'Desvincular'
      )
      .pipe(
        first(),
        filter(confirmado => confirmado)
      )
      .subscribe(() =>
        this.executar(this.meetingsService.unlinkMeeting(meeting.id, link.id), {
          sucesso: 'Vínculo desfeito.',
          falha: 'Não foi possível desvincular',
        })
      );
  }

  /** Abre, em outra aba, o link temporário de um arquivo JSON da Recall. */
  protected abrirArtefato(artifact: MeetingArtifact): void {
    this.meetingsService
      .getArtifactUrl(this.meetingId(), artifact)
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resposta => {
          if (resposta?.url) window.open(resposta.url, '_blank', 'noopener');
        },
        error: erro =>
          this.toast.showToastError(
            'Não foi possível abrir o arquivo',
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          ),
      });
  }

  /**
   * Arquivar, restaurar ou excluir (card 3786), com confirmação. Arquivar e restaurar trocam a reunião
   * pela resposta (o selo "Arquivada" acompanha); excluir volta para a lista de onde se veio.
   */
  protected executeLifecycleAction(action: MeetingLifecycleAction): void {
    const meeting = this.meeting();
    if (!meeting || !this.podeAgir()) return;
    const texts = meetingLifecycleTexts(action, meeting);

    this.confirmationService
      .confirm(texts.title, texts.description, texts.variant, undefined, texts.acceptLabel)
      .pipe(
        first(),
        filter(confirmed => confirmed)
      )
      .subscribe(() => {
        const messages = { sucesso: texts.successMessage, falha: texts.failureTitle };
        if (action === 'archive') {
          this.executar(this.meetingsService.archive(meeting.id), messages);
        } else if (action === 'restore') {
          this.executar(this.meetingsService.restore(meeting.id), messages);
        } else {
          this.deleteMeeting(meeting.id, messages);
        }
      });
  }

  /** Exclui e volta para a lista (aba, filtros e página de onde se veio). */
  private deleteMeeting(meetingId: string, messages: { sucesso: string; falha: string }): void {
    this.ocupada.set(true);
    this.meetingsService
      .delete(meetingId)
      .pipe(
        first(),
        finalize(() => this.ocupada.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toast.showToastSuccess(messages.sucesso);
          // A reunião não existe mais: o polling da gravação e dos insights para aqui.
          this.atualizacao?.unsubscribe();
          this.atualizacaoDosInsights?.unsubscribe();
          this.router.navigateByUrl(this.backToListUrl());
        },
        error: error =>
          this.toast.showToastError(
            messages.falha,
            mensagemDoErro(error, 'Tente de novo em alguns instantes.')
          ),
      });
  }

  /** A lista de Meetings como estava; se o detalhe foi aberto de outro lugar, a lista padrão. */
  private backToListUrl(): string {
    const back = this.voltarPara();
    return back.startsWith('/dashboard/meetings') ? back : '/dashboard/meetings';
  }

  protected aoVincular(atualizada: MeetingDto | null): void {
    if (atualizada) this.meeting.set(atualizada);
  }

  private executar(
    acao: Observable<MeetingDto | null>,
    mensagens: { sucesso: string; falha: string }
  ): void {
    this.ocupada.set(true);
    acao
      .pipe(
        first(),
        finalize(() => this.ocupada.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: atualizada => {
          this.toast.showToastSuccess(mensagens.sucesso);
          if (atualizada) this.meeting.set(atualizada);
        },
        error: erro =>
          this.toast.showToastError(
            mensagens.falha,
            mensagemDoErro(erro, 'Tente de novo em alguns instantes.')
          ),
      });
  }
}
